import { addBreadcrumb, type SeverityLevel } from '@sentry/react';
import type { CreateLoggerOptions, Logger, LogLevel } from './types.js';

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  silent: 4,
};

const METHODS = ['debug', 'info', 'warn', 'error'] as const;

const SENTRY_LEVEL: Record<(typeof METHODS)[number], SeverityLevel> = {
  debug: 'debug',
  info: 'info',
  warn: 'warning',
  error: 'error',
};

function defaultLevel(): LogLevel {
  try {
    return import.meta.env.PROD ? 'warn' : 'debug';
  } catch {
    return 'warn';
  }
}

// noop placeholder for suppressed log levels
// biome-ignore lint/suspicious/noEmptyBlockStatements: intentional no-op
function noop() {}

export function createLogger(options?: CreateLoggerOptions): Logger {
  const threshold = LEVEL_ORDER[options?.level ?? defaultLevel()];
  const prefix = options?.name;
  const category = options?.name ?? 'logger';

  const logger = {} as Logger;
  for (const method of METHODS) {
    if (LEVEL_ORDER[method] < threshold) {
      logger[method] = noop;
    } else {
      logger[method] = (message: string, data?: Record<string, unknown>) => {
        const msg = prefix ? `${prefix} ${message}` : message;
        data !== undefined ? console[method](msg, data) : console[method](msg);
        addBreadcrumb({
          level: SENTRY_LEVEL[method],
          message: msg,
          data,
          category,
        });
      };
    }
  }

  return logger;
}

/** Default logger singleton. Uses 'warn' in production, 'debug' otherwise. */
export const logger: Logger = createLogger();
